import { apiClient } from './client'

export type RequestCategory = 'attendance' | 'leave' | 'finance' | 'other'

export interface RequestTypeConfig {
  key: string
  label: string
  category: RequestCategory
  flow: 'attendance' | 'leave' | 'finance' | 'other'
  description: string
  feature?: string
  needs?: string[]
}

export interface RequestTypesResponse {
  categories: { key: RequestCategory; label: string }[]
  types: RequestTypeConfig[]
  all_types: RequestTypeConfig[]
  disabled: string[]
  pf_purposes: string[]
}

/** A finance/other request (medical, loan, PF, travel, general …). */
export interface EmployeeRequest {
  id: string
  source: 'request'
  user_id: string
  user_name?: string | null
  kind: string
  kind_label: string
  category: RequestCategory
  start_date?: string | null
  end_date?: string | null
  amount?: number | null
  installments?: number | null
  title?: string | null
  details: string
  attachment_url?: string | null
  status: string
  stage: 'manager' | 'hr'
  waiting_on?: string | null
  manager_reviewer_name?: string | null
  manager_note?: string | null
  reviewer_name?: string | null
  review_note?: string | null
  reviewed_at?: string | null
  deduction_start?: string | null
  created_at: string
  loan?: { repaid: number; outstanding: number; instalment: number } | null
}

/** One pending item in the approver inbox, whatever table it lives in. */
export interface InboxItem {
  id: string
  source: 'request' | 'regularization' | 'overtime' | 'timed'
  kind: string
  kind_label: string
  category: RequestCategory
  user_id: string
  user_name?: string | null
  start_date?: string | null
  end_date?: string | null
  amount?: number | null
  installments?: number | null
  hours?: number
  title?: string | null
  details: string
  checkin?: string | null
  checkout?: string | null
  user_timezone?: string | null
  wfh?: boolean
  attachment_url?: string | null
  stage?: 'manager' | 'hr'
  manager_reviewer_name?: string | null
  manager_note?: string | null
  /** false when approving only forwards a money request to HR / payroll */
  final_on_approve?: boolean
  status: string
  created_at: string
}

export interface RequestCreate {
  kind: string
  start_date?: string | null
  end_date?: string | null
  amount?: number | null
  installments?: number | null
  title?: string | null
  details: string
  attachment_url?: string | null
}

export async function getRequestTypes(): Promise<RequestTypesResponse> {
  const { data } = await apiClient.get<RequestTypesResponse>('/requests/types')
  return data
}

export async function setDisabledRequestTypes(disabled: string[]): Promise<{ disabled: string[] }> {
  const { data } = await apiClient.put<{ disabled: string[] }>('/requests/types', { disabled })
  return data
}

export async function listMyEmployeeRequests(): Promise<EmployeeRequest[]> {
  const { data } = await apiClient.get<EmployeeRequest[]>('/requests/mine')
  return data
}

export async function createEmployeeRequest(body: RequestCreate): Promise<EmployeeRequest> {
  const { data } = await apiClient.post<EmployeeRequest>('/requests', body)
  return data
}

export async function cancelEmployeeRequest(id: string): Promise<EmployeeRequest> {
  const { data } = await apiClient.post<EmployeeRequest>(`/requests/${id}/cancel`, {})
  return data
}

export async function getInbox(): Promise<{ items: InboxItem[]; leave_pending: number; can_review: boolean }> {
  const { data } = await apiClient.get('/requests/inbox')
  return data
}

export async function getInboxSummary(): Promise<{ can_review: boolean; pending: number }> {
  const { data } = await apiClient.get('/requests/inbox/summary')
  return data
}

/** Approve/reject any inbox item through the endpoint that owns it. */
export async function reviewInboxItem(item: InboxItem, status: 'approved' | 'rejected', note?: string): Promise<void> {
  const n = note?.trim() || undefined
  if (item.source === 'request') await apiClient.post(`/requests/${item.id}/review`, { status, note: n })
  else if (item.source === 'regularization') await apiClient.post(`/attendance-requests/regularizations/${item.id}/review`, { status, review_note: n })
  else if (item.source === 'overtime') await apiClient.post(`/attendance-requests/overtime/${item.id}/review`, { status, review_note: n })
  else await apiClient.post(`/attendance-approvals/${item.id}/review`, { status, review_note: n })
}
